import { z } from "zod";
import { PLAN_IDS, type PlanId } from "./subscription.js";

/**
 * สถานะคำสั่งซื้อ
 * - ผ่าน Opn: pending, paid, expired, failed, mismatch
 * - ชำระด้วย QR แม่มณี (ตรวจโดยคน): awaiting_payment, pending_review, approved, rejected, expired, cancelled
 * paid หมายถึงผู้ให้บริการชำระเงินยืนยันแล้ว ส่วน approved คือแอดมินอนุมัติหลังตรวจเงินเข้าเอง
 */
export const ORDER_STATUSES = ["pending", "paid", "expired", "failed", "mismatch", "awaiting_payment", "pending_review", "approved", "rejected", "cancelled"] as const;
export type OrderStatus = (typeof ORDER_STATUSES)[number];

export const PAYMENT_METHODS = ["opn_promptpay", "manual_maemanee_qr"] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

/** อายุคำสั่งซื้อที่รอชำระของ QR แม่มณี (ชั่วโมง) ส่วน pending_review ไม่หมดอายุ */
export const MANUAL_ORDER_TTL_HOURS = 24;

/** สถานะที่ถือว่าชำระสำเร็จและเปิดสิทธิ์แล้ว (ใช้กับใบเสร็จ รายงานรายได้ และรายการชำระ) */
export const SETTLED_STATUSES: readonly OrderStatus[] = ["paid", "approved"];

/**
 * การเปลี่ยนสถานะของคำสั่งซื้อแบบ QR แม่มณี (ฝั่ง server เป็นผู้ตัดสินเสมอ)
 * approved ย้อนกลับไม่ได้ การแก้ไขต้องทำผ่านการกำหนดแพ็กเกจโดย Admin ที่มี audit
 * expired/cancelled/rejected กลับมา pending_review ได้เฉพาะ Admin พร้อมเหตุผล (เงินโอนล่าช้าหรือตัดสินพลาด)
 */
export const MANUAL_TRANSITIONS: Record<string, readonly OrderStatus[]> = {
  awaiting_payment: ["pending_review", "expired", "cancelled"],
  pending_review: ["approved", "rejected"],
  expired: ["pending_review"],
  cancelled: ["pending_review"],
  rejected: ["pending_review"],
  approved: [],
};
export const canTransitionManual = (from: OrderStatus, to: OrderStatus): boolean => (MANUAL_TRANSITIONS[from] ?? []).includes(to);

/** POST /admin/orders/{id}/approve และ reject: ต้องอ้างเวอร์ชันของการแจ้งชำระที่ตรวจ กันตัดสินรายการเก่า */
export const approveOrderRequestSchema = z.object({ reviewVersion: z.number().int().min(1).max(1000) });
export const rejectOrderRequestSchema = z.object({
  reviewVersion: z.number().int().min(1).max(1000),
  /** เหตุผลที่แสดงให้ลูกค้าเห็น ห้ามใส่ข้อมูลภายในหรือข้อมูลส่วนบุคคลของผู้อื่น */
  reason: z.string().trim().min(3).max(300),
});
export const reopenOrderRequestSchema = z.object({ reason: z.string().trim().min(3).max(300) });

/** อายุ QR (นาที) ตาม design */
export const QR_TTL_MINUTES = 15;

/** ราคาขั้นต่ำของแพ็กเกจเสียเงิน (บาท): Opn PromptPay รับ charge ตั้งแต่ 20 บาทขึ้นไป */
export const MIN_PAID_PRICE_THB = 20;

/** แปลงบาทเป็นสตางค์ (หน่วยที่ Opn ใช้) แบบกัน floating point */
export const toSatang = (thb: number): number => Math.round((thb + Number.EPSILON) * 100);

/** POST /orders  ผู้ซื้อระบุแพ็กเกจเท่านั้น ราคาอ่านจากตาราง plans ฝั่ง server */
export const createOrderRequestSchema = z.object({ plan: z.enum(PLAN_IDS).refine((p) => p !== "free", "free_not_purchasable") });

export interface OrderDto {
  orderId: string;
  plan: PlanId;
  amount_thb: number;
  status: OrderStatus;
  /** QR PromptPay (ภาพ) จาก provider */
  qr?: { imageUrl: string };
  expiresAt: string;
  /** เวลาสร้างคำสั่งซื้อ (ใช้เรียงและแสดงในประวัติการชำระเงิน) */
  createdAt?: string;
  paidAt?: string;
  /** เมื่อ paid: ผลต่อ subscription */
  activated?: { kind: "new" | "renew" | "upgrade"; periodEnd: string };
  receiptNo?: string;
  mismatch?: { expected_thb: number; received_thb: number };
  /** วิธีชำระของคำสั่งซื้อนี้ (รายการเก่าที่ไม่มีค่า = opn_promptpay) */
  method?: PaymentMethod;
  /** QR แม่มณี: เวลาที่ลูกค้าแจ้งชำระล่าสุด (ไม่ใช่เวลาที่ธนาคารยืนยัน) และเวอร์ชันของการแจ้ง */
  submittedAt?: string;
  reviewVersion?: number;
  /** เหตุผลที่แอดมินระบุให้ลูกค้าเห็นเมื่อ rejected */
  rejectionReason?: string;
  reviewedAt?: string;
}

export interface PlanDto {
  plan: PlanId;
  price_thb: number;
  estimates_per_month: number;
  saved_jobs_limit: number | null;
  quotation: boolean;
  gate_per_day: number;
}
